import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import FooterTwo from '../../components/FooterTwo';
import ApplyModal from './components/ApplyModal';
import { COURSES_ENDPOINT, APPLICATIONS_ENDPOINT } from '../../config/api';
import { Styles } from './styles/course.js';
import { useAuth } from '../../context/AuthContext';

const initialApplicationStatus = { state: 'idle', message: '' };

const formatCurrency = (amount, currency) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
        return null;
    }
    const formattedAmount = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
    return `${currency || ''} ${formattedAmount}`.trim();
};

const CourseDetails = () => {
    const { courseId } = useParams();
    const { user } = useAuth();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [applicationStatus, setApplicationStatus] = useState(initialApplicationStatus);
    const [appliedCourseIds, setAppliedCourseIds] = useState(() => new Set());

    useEffect(() => {
        const fetchCourse = async () => {
            if (!courseId) {
                setError('No course selected.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');
            try {
                const response = await fetch(`${COURSES_ENDPOINT}/${courseId}`);
                if (!response.ok) {
                    throw new Error('Unable to load course details right now.');
                }
                const data = await response.json();
                setCourse(data);
            } catch (err) {
                console.error(err);
                setError(err.message || 'We could not load this course.');
                setCourse(null);
            } finally {
                setLoading(false);
            }
        };

        fetchCourse();
    }, [courseId]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const loadApplications = async () => {
            if (!user?.email) {
                if (isMounted) {
                    setAppliedCourseIds(new Set());
                }
                return;
            }

            try {
                const response = await fetch(`${APPLICATIONS_ENDPOINT}?email=${encodeURIComponent(user.email)}`, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error('Unable to fetch applications');
                }

                const payload = await response.json();
                if (!isMounted) {
                    return;
                }

                const nextIds = new Set();
                (payload.applications || []).forEach((application) => {
                    const courseRef = application.course || application.courseId;
                    const applicationCourseId =
                        (courseRef && typeof courseRef === 'object' && (courseRef._id || courseRef.id)) ||
                        (typeof courseRef === 'string' ? courseRef : null) ||
                        application.course;
                    if (applicationCourseId) {
                        nextIds.add(String(applicationCourseId));
                    }
                });

                setAppliedCourseIds(nextIds);
            } catch (err) {
                if (!controller.signal.aborted && isMounted) {
                    setAppliedCourseIds(new Set());
                }
            }
        };

        loadApplications();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [user?.email]);

    const handleApplyClick = () => {
        const currentCourseId = course?._id ? String(course._id) : '';
        if (!currentCourseId || appliedCourseIds.has(currentCourseId)) {
            return;
        }
        setApplicationStatus(initialApplicationStatus);
        setShowApplyModal(true);
    };

    const handleCloseModal = () => {
        setShowApplyModal(false);
        setApplicationStatus(initialApplicationStatus);
    };

    const handleApplicationSubmit = async (payload) => {
        if (!course) {
            return;
        }

        setApplicationStatus({ state: 'submitting', message: '' });
        try {
            const response = await fetch(APPLICATIONS_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    courseId: course._id,
                    ...payload
                })
            });

            if (!response.ok) {
                const { message } = await response.json();
                throw new Error(message || 'Unable to submit your application.');
            }

            setApplicationStatus({ state: 'success', message: 'Application submitted successfully.' });
            setAppliedCourseIds((prev) => {
                const next = new Set(prev);
                if (course?._id) {
                    next.add(String(course._id));
                }
                return next;
            });
        } catch (err) {
            console.error(err);
            setApplicationStatus({
                state: 'error',
                message: err.message || 'We could not submit your application. Please try again.'
            });
        }
    };

    const currentCourseId = course?._id ? String(course._id) : '';
    const hasApplied = currentCourseId ? appliedCourseIds.has(currentCourseId) : false;

    const tuitionLabel = useMemo(
        () => formatCurrency(course?.tuitionFeePerYear ?? course?.tuitionFeePerSemester, course?.currency),
        [course]
    );

    const highlightCards = useMemo(() => {
        if (!course) return [];
        return [
            {
                icon: 'las la-map-marker-alt',
                label: 'Location',
                value: [course.location, course.campus].filter(Boolean).join(', ')
            },
            {
                icon: 'las la-user-graduate',
                label: 'Level',
                value: course.levelOfStudy
            },
            {
                icon: 'las la-clock',
                label: 'Duration',
                value: course.duration ? `${course.duration} months` : null
            },
            {
                icon: 'las la-chalkboard-teacher',
                label: 'Study Mode',
                value: course.portalOrDirect
            },
            {
                icon: 'las la-medal',
                label: 'Scholarship',
                value: course.scholarship || null
            }
        ].filter((item) => item.value);
    }, [course]);

    const admissionFacts = useMemo(() => {
        if (!course) return [];
        return [
            { label: 'University', value: course.universityName },
            { label: 'Private/Public', value: course.privateOrPublic },
            { label: 'Intakes', value: course.intakes },
            { label: 'Deadlines', value: course.deadlines }
        ].filter((item) => item.value);
    }, [course]);

    const requirementFacts = useMemo(() => {
        if (!course) return [];
        return [
            { label: '15 Year Degree Accepted', value: course.fifteenYearDegreeAccepted === true ? 'Yes' : course.fifteenYearDegreeAccepted === false ? 'No' : null },
            { label: 'Minimum GPA (out of 4)', value: course.gpaOutOf4 },
            { label: 'Standardized Tests', value: course.standardizedTests },
            { label: 'IELTS', value: course.ielts },
            { label: 'TOEFL', value: course.toefl },
            { label: 'Duolingo', value: course.duolingo },
            { label: 'PTE', value: course.pte },
            { label: 'Backlogs Accepted', value: course.backlogs },
            { label: 'Medium of Instruction', value: course.moiAccepted === true ? 'Accepted' : course.moiAccepted === false ? 'Not accepted' : null },
            { label: 'WES Required', value: course.wesRequired === true ? 'Yes' : course.wesRequired === false ? 'No' : null }
        ].filter((item) => item.value);
    }, [course]);

    const feeFacts = useMemo(() => {
        if (!course) return [];
        return [
            {
                icon: 'las la-wallet',
                label: 'Application Fee',
                value: formatCurrency(course.applicationFee, course.currency)
            },
            {
                icon: 'las la-file-invoice-dollar',
                label: 'Admission Fee',
                value: formatCurrency(course.admissionFee, course.currency)
            },
            {
                icon: 'las la-book',
                label: 'Tuition per Course',
                value: formatCurrency(course.tuitionFeePerCourse, course.currency)
            },
            {
                icon: 'las la-university',
                label: 'Credits',
                value: course.credits
            },
            {
                icon: 'las la-money-check',
                label: 'Per Credit Rate',
                value: formatCurrency(course.perCreditRate, course.currency)
            }
        ].filter((item) => item.value);
    }, [course]);

    const tuitionDisplay = tuitionLabel || 'Tuition on request';

    return (
        <div className="main-wrapper course-details-page">

            <HeaderTwo />

            <BreadcrumbBox title={course?.programName || 'Course Details'} />

            <Styles>
                <section className="course-details-area">
                    <Container>
                        {loading && (
                            <Row>
                                <Col>
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status" size="sm" />
                                        <span className="ml-2">Loading course…</span>
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {!loading && error && (
                            <Row>
                                <Col>
                                    <Alert variant="danger">{error}</Alert>
                                </Col>
                            </Row>
                        )}

                        {!loading && !error && course && (
                            <Row className="course-details-wrapper">
                                <Col lg="8" md="7" sm="12">
                                    <div className="detail-card course-summary-card">
                                        <div className="summary-header">
                                            {course.levelOfStudy && (
                                                <span className="summary-tag">{course.levelOfStudy}</span>
                                            )}
                                            <h2>{course.programName}</h2>
                                            {course.universityName && (
                                                <p className="summary-subtitle">{course.universityName}</p>
                                            )}
                                        </div>
                                        <div className="summary-meta">
                                            {highlightCards.map((item) => (
                                                <div className="summary-meta-item" key={item.label}>
                                                    <div className="icon">
                                                        <i className={item.icon}></i>
                                                    </div>
                                                    <div className="meta-content">
                                                        <span className="meta-label">{item.label}</span>
                                                        <span className="meta-value">{item.value}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {course.concentration && (
                                        <div className="detail-card">
                                            <h4>Program Overview</h4>
                                            <p>{course.concentration}</p>
                                        </div>
                                    )}

                                    {course.programDetails && (
                                        <div className="detail-card">
                                            <h4>Program Details</h4>
                                            <iframe
                                                title="Program Details"
                                                className="program-details-frame"
                                                srcDoc={course.programDetails}
                                                sandbox="allow-scripts"
                                            />
                                        </div>
                                    )}

                                    {admissionFacts.length > 0 && (
                                        <div className="detail-card">
                                            <h4>Program Snapshot</h4>
                                            <ul className="detail-list detail-left">
                                                {admissionFacts.map((fact) => (
                                                    <li key={fact.label}>
                                                        <div className="detail-left-label">
                                                            <i className="las la-check-circle"></i>
                                                            <span>{fact.label}</span>
                                                        </div>
                                                        <strong>{fact.value}</strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {requirementFacts.length > 0 && (
                                        <div className="detail-card">
                                            <h4>Entry Requirements</h4>
                                            <ul className="detail-list detail-left">
                                                {requirementFacts.map((fact) => (
                                                    <li key={fact.label}>
                                                        <div className="detail-left-label">
                                                            <i className="las la-arrow-right"></i>
                                                            <span>{fact.label}</span>
                                                        </div>
                                                        <strong>{fact.value}</strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="detail-card actions-card">
                                        <div className="d-flex flex-wrap align-items-center justify-content-between">
                                            <div className="cta-text">
                                                <h5>Ready to apply?</h5>
                                                <p>Submit your application to begin the admissions process.</p>
                                            </div>
                                            <div className="cta-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-success mr-2 mb-2"
                                                    onClick={handleApplyClick}
                                                    disabled={hasApplied}
                                                >
                                                    {hasApplied ? 'Applied' : 'Apply Now'}
                                                </button>
                                                <Link className="btn btn-outline-secondary mb-2" to={process.env.PUBLIC_URL + "/courses"}>
                                                    Back to Courses
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col lg="4" md="5" sm="12">
                                    <aside className="course-details-sidebar">
                                        <div className="sidebar-card pricing-card">
                                            <span className="label">Tuition from</span>
                                            <p className="price">{tuitionDisplay}</p>
                                            <button
                                                type="button"
                                                className="btn btn-success btn-block mt-3"
                                                onClick={handleApplyClick}
                                                disabled={hasApplied}
                                            >
                                                {hasApplied ? 'Applied' : 'Apply Now'}
                                            </button>
                                        </div>
                                        {feeFacts.length > 0 && (
                                            <div className="sidebar-card info-card">
                                                <h5>Tuition & Fees</h5>
                                                <ul className="detail-list compact">
                                                    {feeFacts.map((fact) => (
                                                        <li key={fact.label}>
                                                            <i className={fact.icon}></i>
                                                            <span>{fact.label}</span>
                                                            <strong>{fact.value}</strong>
                                                        </li>
                                                    ))}
                                                </ul>
                                                {course.scholarship && (
                                                    <div className="scholarship-note mt-3">
                                                        <i className="las la-gift"></i>
                                                        Scholarship opportunity: <strong>{course.scholarship}</strong>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="sidebar-card info-card">
                                            <h5>Need help?</h5>
                                            <p>If you have questions about this program, our admissions team is here to help.</p>
                                            <Link className="btn btn-outline-primary btn-block" to={process.env.PUBLIC_URL + "/contact"}>
                                                Contact Us
                                            </Link>
                                        </div>
                                        <div className="sidebar-card info-card">
                                            <h5>Program Snapshot</h5>
                                            <ul className="detail-list compact">
                                                {highlightCards.slice(0, 3).map((item) => (
                                                    <li key={item.label}>
                                                        <i className={item.icon}></i>
                                                        <span>{item.label}</span>
                                                        <strong>{item.value}</strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </aside>
                                </Col>
                            </Row>
                        )}
                    </Container>
                </section>
            </Styles>

            <FooterTwo />

            <ApplyModal
                show={showApplyModal}
                course={course}
                status={applicationStatus}
                onClose={handleCloseModal}
                onSubmit={handleApplicationSubmit}
            />
        </div>
    );
};

export default CourseDetails;
